import moment from 'moment';
import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { ContextMenu, Pill, ShuffleIcon, SignInIcon, tokens } from '@treams/treams-ui';

import { useJobTitleName } from '../../../job/useJobTitleName';

const Transition = styled(Box)`
	position: relative;
	background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
	width: 24px;
	height: 24px;
	padding: 4px;
	border-radius: 16px;
	box-sizing: border-box;
	flex-shrink: 0;

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: ${(props) => props.theme.colors.primary};
	}
`;

const JobPositionTitle = styled(Box)`
	font-size: 14px;
	font-weight: bold;
`;

const JobPositionLevel = styled(Box)`
	font-size: 12px;
	color: ${tokens.text.secondary};
`;

const Item = styled(Flex)<{ active: boolean }>`
	height: 48px;
	border-radius: 6px;
	justify-content: center;
	flex-direction: column;
	padding: 0px 12px;
	cursor: pointer;

	${(props) =>
		props.active
			? `
        background: #ffffff;
        border: 1px solid #c2cad2;
        box-shadow: 0px 2px 4px rgba(194, 202, 210, 0.25);
    `
			: `
        border: 1px solid #D8E0E8;
    `}
`;

const TransitionLabel = styled(Flex)`
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	flex-direction: column;
	white-space: nowrap;
	width: max-content;
	top: 42px;
	margin-top: 8px;
	align-items: center;
`;

const TransitionDate = styled(Box)`
	color: ${tokens.text.secondary};
	line-height: 30px;
	height: 30px;
	white-space: nowrap;
`;

function JobPositionMenu(props: { user: graphql.User; position: graphql.JobPosition }) {
	const { t } = useTranslation();

	const [archive] = graphql.useArchiveJobPositionMutation();
	const [update] = graphql.useUpdateJobPositionMutation();

	const startDate = props.position.startDate ? moment(props.position.startDate) : undefined;
	const endDate = props.position.endDate ? moment(props.position.endDate) : undefined;
	const isFutureJobPosition = startDate?.isAfter(moment());
	const canDelete = Boolean(endDate || isFutureJobPosition);

	return (
		<ContextMenu>
			<ContextMenu.Item
				type="destructive"
				title={
					canDelete
						? t('settings.employees.jobPositions.job.deleteFunction')
						: t('settings.employees.jobPositions.job.terminateFunction')
				}
				onClick={async () => {
					if (canDelete) {
						await archive({ variables: { input: { id: props.position.id } } });
						return;
					}

					await update({
						variables: {
							input: {
								id: props.position.id,
								jobTitleID: props.position.jobTitle.id,
								startDate: props.position.startDate,
								userID: props.user.id,
								endDate: new Date(),
								externalReferenceID: props.position.externalReferenceID,
								jobLevelID: props.position.jobLevel?.id,
							},
						},
					});
				}}
			/>
		</ContextMenu>
	);
}

export default function JobPosition(props: {
	user: graphql.User;
	position: graphql.JobPosition;
	index: number;
}) {
	const { t } = useTranslation();

	const { localize } = useLocalize();
	const jobTitleName = useJobTitleName();

	const startDate = props.position.startDate ? moment(props.position.startDate) : undefined;
	const endDate = props.position.endDate ? moment(props.position.endDate) : undefined;

	return (
		<>
			<Transition mr={2} flexGrow={0} flexShrink={0}>
				{props.index === 0 ? <SignInIcon size={16} /> : <ShuffleIcon size={16} />}
				<TransitionLabel>
					<TransitionDate>{moment(props.position.startDate).format('LL')}</TransitionDate>
					<Pill tint={props.index === 0 ? undefined : 'primary'}>
						{props.index === 0
							? t('settings.employees.jobPositions.transition.joined')
							: t('settings.employees.jobPositions.transition.changed')}
					</Pill>
				</TransitionLabel>
			</Transition>
			<Box flexGrow={0} flexShrink={0}>
				<ContextMenu.Target menu={<JobPositionMenu user={props.user} position={props.position} />}>
					<Item
						active={
							!props.position.isArchived &&
							(startDate?.isBefore(moment()) ?? true) &&
							(endDate?.isAfter(moment()) ?? true)
						}
						mr={2}
					>
						<JobPositionTitle>{jobTitleName(props.position.jobTitle)}</JobPositionTitle>
						{props.position.jobLevel && (
							<JobPositionLevel>{localize(props.position.jobLevel.title).text}</JobPositionLevel>
						)}
					</Item>
				</ContextMenu.Target>
			</Box>
		</>
	);
}
